/**
 * propertySearch.js — recherche rapide d'un bien dans le portefeuille : source UNIQUE.
 *
 * Lot AE1/AE2. Le filtre de la liste testait huit chemins dont quatre morts
 * (`property_reference`, `taxonomy.objet|profil|sous_profil` : la clé `taxonomy` n'existe sur
 * aucun bien, la taxonomie est à la RACINE du document). La recherche paraissait donc
 * fonctionner alors qu'elle ratait la majorité des saisies de taxonomie.
 *
 * Modèle suivi : `utils/contactIdentity.js` (contactMatchesQuery) — un helper, testable, réutilisé.
 *
 * ⚠️ Champs volontairement EXCLUS :
 *  - le descriptif (objet multilingue `descriptions.Web.Fra`, volumineux et bruyant) ;
 *
 * lot AG2 — `activite_precise` (« Libellé d'affichage ») est en revanche CHERCHÉ : il remplace
 * l'activité affichée sur la carte (« Pizzeria » pour un bien de catégorie « Restaurant »).
 * Le critère n'est pas le nombre de biens renseignés mais la règle suivante : ce qui est
 * affiché à l'écran doit être cherchable.
 *  - le nom du propriétaire (absent de la projection de la liste — l'y mettre ferait transiter
 *    les coordonnées des vendeurs vers le navigateur à chaque ouverture de l'écran).
 */
import taxonomyConfig from '../constants/taxonomyConfig.json';
import { normalizeSearch } from './contactIdentity';

/**
 * Codes de taxonomie dont le LIBELLÉ (ou le code) contient la saisie.
 *
 * ⚠️ Aucune table de correspondance libellé → code n'est écrite ici : les libellés sont lus
 * directement dans `constants/taxonomyConfig.json`, seule source de vérité de la taxonomie du
 * projet (le backend lit le MÊME fichier, cf. server.py `_load_taxonomy`). Une copie serait un
 * registre parallèle de plus.
 *
 * « licen » → licence_iv, licence_iii, licence_exploitation… (correspondance partielle).
 */
export const codesTaxonomiePourSaisie = (rawQuery) => {
  const q = normalizeSearch(rawQuery).trim();
  if (!q) return [];
  const codes = new Set();
  const retenir = (code, label) => {
    if (!code) return;
    if (normalizeSearch(code).includes(q) || normalizeSearch(label || '').includes(q)) {
      codes.add(String(code).toLowerCase());
    }
  };
  const operations = taxonomyConfig?.operations || {};
  Object.entries(operations).forEach(([, opData]) => {
    Object.entries(opData?.objets || {}).forEach(([objet, objData]) => {
      retenir(objet, objData?.label);
      Object.entries(objData?.profils || {}).forEach(([profil, profData]) => {
        retenir(profil, profData?.label);
        Object.entries(profData?.sous_profils || {}).forEach(([sousProfil, label]) => {
          retenir(sousProfil, typeof label === 'string' ? label : label?.label);
        });
      });
    });
  });
  return Array.from(codes);
};

/**
 * Le bien correspond-il à la saisie ? Champs cherchés (tous présents dans la projection de
 * GET /properties/list, aucune modification serveur nécessaire) :
 * @param {string[]} [codesDejaCalcules] codes de taxonomie déjà calculés pour cette saisie.
 *
 * reference · id · numero_mandat · bien_general.titre_annonce · bien_general.type_bien
 * bien_general.ville_generale · bien_general.secteur · enseigne.enseigne · bien_general.enseigne
 * + taxonomie (objet / profil / sous_profil) par ses libellés.
 */
export const propertyMatchesQuery = (property, rawQuery, codesDejaCalcules) => {
  const q = normalizeSearch(rawQuery).trim();
  if (!q) return true;
  const p = property || {};
  const bg = p.bien_general || {};

  const texte = [
    p.reference,
    p.id,
    p.numero_mandat,
    p.activite_precise,   // lot AG2 — libellé affiché sur la carte
    bg.titre_annonce,
    bg.type_bien,
    bg.ville_generale,
    // `bien_general.ville_generale` est vide sur certains portefeuilles : la ville réelle
    // s'y trouve alors dans `informations_confidentielles.ville` (déjà projetée par la route
    // de liste). Sans ce second chemin, la recherche par ville — la plus fréquente — est morte.
    p.informations_confidentielles?.ville,
    bg.secteur,
    // L'enseigne est stockée aux DEUX emplacements (doublon existant, non nettoyé ici).
    p.enseigne?.enseigne,
    bg.enseigne,
  ]
    .filter(Boolean)
    .map(normalizeSearch);
  if (texte.some((v) => v.includes(q))) return true;

  // lot AF3 — les codes ne dépendent QUE de la saisie : l'appelant qui filtre une liste les
  // calcule une seule fois et les passe ici (445 nœuds × 1 186 biens à chaque caractère tapé,
  // sinon). Sans ce paramètre, le calcul interne est conservé : la fonction reste utilisable
  // seule, et les tests du lot AE n'ont pas à changer.
  const codes = codesDejaCalcules || codesTaxonomiePourSaisie(q);
  if (codes.length === 0) return false;
  const codesDuBien = [p.objet, p.profil, p.sous_profil]
    .filter(Boolean)
    .map((c) => String(c).toLowerCase());
  return codesDuBien.some((c) => codes.includes(c));
};

/** Champs cherchés, pour le relevé et les tests. */
export const CHAMPS_RECHERCHE_LISTE = [
  'reference', 'id', 'numero_mandat', 'activite_precise',
  'bien_general.titre_annonce', 'bien_general.type_bien',
  'bien_general.ville_generale', 'informations_confidentielles.ville',
  'bien_general.secteur', 'enseigne.enseigne', 'bien_general.enseigne',
  'objet', 'profil', 'sous_profil',
];
